import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import PropTypes from 'prop-types'

import Btn from '../../../../../UI/Btn'
import VisibilityAccess from '../../../../VisibilityAccess'
import { TRoles } from '../../../../../shared/const'
import { stateLabel } from '../../../../../shared/helpers'
import { useAvatarUrl } from '../../../../../shared/useAvatarUrls'

import styles from '../style.module.scss'
import { formatDate } from '../../../../../shared/datetime'

/**
 * Reiter „Basisinformationen": Stammdaten des Unternehmens.
 *
 * Der Firmenname ist das einzige Feld, das die Verwaltung hier aendern kann —
 * alles andere pflegen die Schueler:innen im eigenen Firmenprofil. Mit
 * `readOnly` (Sicht der Lehrkraft) faellt auch das weg: Dieselben Angaben,
 * nichts zu aendern.
 *
 * **Vollstaendig, nicht ausgewaehlt.** Logo, Website und soziale Netzwerke
 * fehlten hier bis 08.08.2026 bzw. standen unter „Kontaktdaten". Das Logo war
 * damit im Admin-Bereich ueberhaupt nicht zu sehen, obwohl es auf jeder
 * oeffentlichen Darstellung der Junior Company auftaucht. Sie gehoeren zu dem,
 * was das Unternehmen ausmacht — die Kontaktdaten daneben sagen nur, wie man es
 * erreicht.
 */
export default function BasicInfoTab(props) {
	const { t } = useTranslation()
	const { companyInfo, readOnly, isNameChanging, setIsNameChanging, newNameValue, setNewNameValue, onSaveName } = props

	// Ohne Aenderung gibt es auch keinen Zwischenstand: Der Name kommt dann
	// unmittelbar vom Unternehmen und nicht aus dem Eingabefeld darueber.
	const name = readOnly ? companyInfo?.name : newNameValue

	// Das Logo wird angemeldet geholt (siehe shared/useAvatarUrls); ein <img src>
	// koennte den Anmelde-Kopfeintrag nicht mitschicken.
	const logoUrl = useAvatarUrl(companyInfo?.logoHashName)

	return (
		<section className={styles.block}>
			<h3 className={styles.blockTitle}>{t('Master data')}</h3>
			<div className={styles.fieldGrid}>
				<div className={clsx(styles.readOnlyInput, styles.fieldWide)}>
					<h3 className={styles.readOnlyLabel}>{t('Company name')}</h3>
					<div className={styles.fieldWithAction}>
						{!isNameChanging ? (
							<div className={styles.readOnlyText}>{name}</div>
						) : (
							<input
								className={clsx(styles.input, styles.readOnlyText)}
								value={newNameValue || ''}
								onChange={e => setNewNameValue(e.target.value)}
								name="name_change"
							/>
						)}
						{!readOnly && (
							<div className={styles.fieldActions}>
								<VisibilityAccess access={[TRoles.ADMIN, TRoles.FEDERAL_ADMIN]}>
									<Btn
										className={styles.btnVerify}
										iconClassName={styles.btnIconReset}
										type="button"
										icon={isNameChanging ? 'save' : 'edit'}
										iconOrder={0}
										iconSize={{ w: 19, h: 19 }}
										iconColor="#008b9c"
										onClick={() => (isNameChanging ? onSaveName() : setIsNameChanging(true))}
									>
										{isNameChanging ? t('save') : t('Update Name')}
									</Btn>
								</VisibilityAccess>
							</div>
						)}
					</div>
				</div>
				<div className={styles.readOnlyInput}>
					<h3 className={styles.readOnlyLabel}>{t('Company type')}</h3>
					<div className={styles.readOnlyText}>{companyInfo?.companyType}</div>
				</div>
				<div className={styles.readOnlyInput}>
					<h3 className={styles.readOnlyLabel}>{t('branch')}</h3>
					<div className={styles.readOnlyText}>{t(companyInfo?.branch)}</div>
				</div>
				<div className={styles.readOnlyInput}>
					<h3 className={styles.readOnlyLabel}>{t('state')}</h3>
					<div className={styles.readOnlyText}>{stateLabel(companyInfo?.state)}</div>
				</div>
				<div className={styles.readOnlyInput}>
					<h3 className={styles.readOnlyLabel}>{t('Registration date')}</h3>
					<div className={styles.readOnlyText}>
						{formatDate(companyInfo?.createdAt) || '—'}
					</div>
				</div>
				{/* Nur bei einer eingereichten Company: Sonst stuende hier ein
				    leeres Feld, das nach einem Fehler aussieht. */}
				{Boolean(companyInfo?.submittedReview) && (
					<div className={styles.readOnlyInput}>
						<h3 className={styles.readOnlyLabel}>{t('Submitted for review on')}</h3>
						<div className={styles.readOnlyText}>
							{formatDate(companyInfo.submittedReview)}
						</div>
					</div>
				)}
				<div className={styles.readOnlyInput}>
					<h3 className={styles.readOnlyLabel}>{t('Logo')}</h3>
					{companyInfo?.logoHashName ? (
						<div className={styles.logoFrame}>
							{/* Solange das Bild noch geholt wird, bleibt der Rahmen leer —
							    ein Platzhaltertext waere in der Sekunde nur Flackern. */}
							{logoUrl ? <img className={styles.logoImage} src={logoUrl} alt={t('Logo')} /> : null}
						</div>
					) : (
						<div className={styles.readOnlyText}>
							<span className={styles.emptyValue}>{t('No logo yet')}</span>
						</div>
					)}
				</div>
				<div className={styles.readOnlyInput}>
					<h3 className={styles.readOnlyLabel}>{t('Website')}</h3>
					<div className={styles.readOnlyText}>
						{companyInfo?.websiteURL ? (
							<a className={styles.fieldLink} href={companyInfo.websiteURL} target="_blank" rel="noreferrer">
								{companyInfo.websiteURL}
							</a>
						) : (
							<span className={styles.emptyValue}>—</span>
						)}
					</div>
				</div>
				<div className={clsx(styles.readOnlyInput, styles.fieldWide)}>
					<h3 className={styles.readOnlyLabel}>{t('Social Media')}</h3>
					<div className={styles.socialContainer}>
						{companyInfo?.socialNet?.length ? (
							companyInfo.socialNet.map(social => (
								<a
									key={social.link || social.name}
									className={styles.socialLink}
									href={social.link}
									target="_blank"
									rel="noreferrer"
								>
									{social.name || social.link}
								</a>
							))
						) : (
							<span className={styles.emptyValue}>—</span>
						)}
					</div>
				</div>
			</div>
		</section>
	)
}

BasicInfoTab.propTypes = {
	companyInfo: PropTypes.object,
	readOnly: PropTypes.bool,
	isNameChanging: PropTypes.bool,
	setIsNameChanging: PropTypes.func,
	newNameValue: PropTypes.string,
	setNewNameValue: PropTypes.func,
	onSaveName: PropTypes.func
}
